import React from 'react';
import * as Icons from 'lucide-react';
import { certifications, companyInfo } from '../utils/mockData';

const CertificationsPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Certifications & Compliance</h1>
            <p className="text-xl text-slate-300">
              Licensed and certified electrical solutions provider committed to safety and quality standards
            </p>
          </div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Current Certifications</h2>
            <p className="text-slate-600">Official business registrations and certifications</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {certifications.map((cert) => {
              const IconComponent = Icons[cert.icon];
              return (
                <div
                  key={cert.id}
                  className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all border border-slate-200"
                >
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <IconComponent className="text-blue-600" size={32} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {cert.title}
                  </h3>
                  
                  <div className="bg-slate-50 px-4 py-2 rounded-lg mb-4 inline-block">
                    <p className="text-sm font-mono text-slate-700">
                      {cert.number}
                    </p>
                  </div>
                  
                  <p className="text-slate-600 text-sm">
                    {cert.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* GST Details Card */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold mb-2">GST Registered Business</h3>
                <p className="text-blue-100 mb-4">
                  Officially registered and compliant with Government of India tax regulations
                </p>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 inline-block">
                  <p className="text-sm text-blue-100 mb-1">GST Number</p>
                  <p className="text-xl font-bold font-mono">{companyInfo.gst}</p>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Icons.FileCheck className="text-white" size={80} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Standards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Safety & Quality Standards
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              All our work complies with industrial safety regulations and quality standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl text-center border border-slate-200">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.ShieldCheck className="text-green-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Safety First</h3>
              <p className="text-slate-600 text-sm">
                All installations meet industrial safety standards
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl text-center border border-slate-200">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.Award className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Quality Assured</h3>
              <p className="text-slate-600 text-sm">
                Premium components and workmanship guarantee
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl text-center border border-slate-200">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.FileText className="text-orange-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Documentation</h3>
              <p className="text-slate-600 text-sm">
                Complete project documentation and certifications
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl text-center border border-slate-200">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.CheckCircle className="text-purple-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Compliance</h3>
              <p className="text-slate-600 text-sm">
                Adherent to all electrical codes and regulations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upload Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icons.Upload className="text-blue-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                Additional Certifications
              </h3>
              <p className="text-slate-600 mb-6">
                Additional electrical licenses and safety certifications will be uploaded and made available for viewing. All certifications are kept current and up-to-date.
              </p>
              <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-8">
                <Icons.FileText className="text-slate-400 mx-auto mb-3" size={48} />
                <p className="text-slate-500 text-sm">
                  Space reserved for electrical contractor license and safety compliance certificates
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <Icons.Shield className="mx-auto mb-6" size={64} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Trust, Our Priority
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            We take compliance seriously. Every project is executed with proper certifications and adherence to safety standards.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all shadow-lg"
          >
            Work With Certified Professionals
            <Icons.ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CertificationsPage;
